/**
 * Il fuoco spaziale, attaccato a un sottoalbero.
 *
 * Le schermate non ascoltano la tastiera: si abbonano a `FocusPort` attraverso questo
 * gancio, e da qui in giù non c'è un solo codice tasto. Cambiare piattaforma vuol dire
 * cambiare l'adattatore, non queste righe (01-ARCHITETTURA §2.4, punto 3).
 *
 * La scelta del candidato la fa il dominio con la formula del prototipo v3 — distanza più
 * due volte e mezzo lo scarto perpendicolare. Qui si fa solo la parte che il dominio non
 * può fare: leggere i rettangoli dallo schermo, **e solo di ciò che è visibile**.
 */

import { useEffect, type RefObject } from 'react';
import { findNextFocus, type Direction, type FocusCandidate } from '@player/domain';
import type { FocusPort, RemoteKey } from '@player/ports';

export interface SpatialFocusOptions {
  /** Caratteri digitati: accesso, ricerca e PIN li accettano, le altre schermate no. */
  readonly onCharacter?: (character: string) => void;
  readonly onErase?: () => void;
  readonly onBack?: () => boolean;
  /** `true` quando questo sottoalbero è quello attivo. Un modale che si apre spegne
   *  quello sotto: con la richiesta del PIN o la conferma di uscita aperte, le frecce si
   *  muovono solo dentro il modale (03-SCHERMATE-TV §1). */
  readonly active?: boolean;
}

export function useSpatialFocus(
  focusPort: FocusPort,
  containerRef: RefObject<HTMLElement | null>,
  options: SpatialFocusOptions = {},
): void {
  const { onCharacter, onErase, onBack, active = true } = options;

  useEffect(() => {
    if (!active) return undefined;

    const move = (direction: Direction): boolean => {
      const container = containerRef.current;
      if (!container) return false;
      const current = container.ownerDocument.activeElement;
      const elements = [...container.querySelectorAll<HTMLElement>('[data-f]')].filter(
        (element) => element !== current && element.offsetParent !== null,
      );
      const currentRect = current instanceof HTMLElement ? focusPort.measure(current) : null;
      if (!currentRect) {
        elements[0]?.focus({ preventScroll: true });
        return elements.length > 0;
      }
      const candidates: FocusCandidate<HTMLElement>[] = [];
      for (const element of elements) {
        const rect = focusPort.measure(element);
        if (rect) candidates.push({ ref: element, rect });
      }
      const next = findNextFocus(currentRect, candidates, direction);
      if (!next) return false;
      next.focus({ preventScroll: true });
      return true;
    };

    const handle = (key: RemoteKey): boolean => {
      switch (key.kind) {
        case 'direction':
          return move(key.direction);
        case 'select': {
          const container = containerRef.current;
          const current = container?.ownerDocument.activeElement;
          if (current instanceof HTMLElement && current.hasAttribute('data-f')) {
            current.click();
            return true;
          }
          return false;
        }
        case 'character':
          if (!onCharacter) return false;
          onCharacter(key.character);
          return true;
        case 'erase':
          if (!onErase) return false;
          onErase();
          return true;
        case 'back':
          return onBack ? onBack() : false;
      }
    };

    return focusPort.subscribe(handle);
  }, [focusPort, containerRef, onCharacter, onErase, onBack, active]);
}

/**
 * Porta il fuoco al primo elemento utile, preferendo l'area principale al menu laterale.
 * «Cambiare schermata riporta il fuoco al primo elemento utile» (03-SCHERMATE-TV §1).
 */
export function focusFirst(container: HTMLElement | null): void {
  if (!container) return;
  const element =
    container.querySelector<HTMLElement>('[data-modal] [data-f]') ??
    container.querySelector<HTMLElement>('[data-main] [data-f]') ??
    container.querySelector<HTMLElement>('[data-f]');
  element?.focus({ preventScroll: true });
}
